import { useCallback, useState } from 'react';
import { Avatar, Icon } from '../../components';
import type { CallRecord, Recording } from '../../types/history';
import './CallDetails.css';
import VideoPlayerDialog from './VideoPlayerDialog';

function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDurationMinutes(mins: number): string {
  if (mins < 1) return '<1 min';
  if (mins < 60) return `${Math.round(mins)} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function getStatusBadgeClass(status: string): string {
  const s = status.toLowerCase();
  if (s.includes('end')) return 'detail-status-badge--ended';
  if (s.includes('ongoing') || s.includes('active'))
    return 'detail-status-badge--ongoing';
  if (s.includes('cancel') || s.includes('miss'))
    return 'detail-status-badge--cancelled';
  return 'detail-status-badge--default';
}

interface CallDetailsProps {
  call: CallRecord;
  onClose: () => void;
}

function CallDetails({ call, onClose }: CallDetailsProps) {
  const [downloadingRids, setDownloadingRids] = useState<Map<string, number>>(
    new Map(),
  );
  const [playingUrl, setPlayingUrl] = useState<string | null>(null);

  const handleDownload = useCallback(async (rec: Recording) => {
    setDownloadingRids(prev => new Map(prev).set(rec.rid, 0));
    try {
      const blob = await new Promise<Blob>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(
          'GET',
          rec.recording_url.replace(
            'recordings-us.cometchat-staging.com',
            'recordings.rtcv5-us.cometchat-staging.com',
          ),
          true,
        );
        xhr.responseType = 'blob';
        xhr.onprogress = e => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 100);
            setDownloadingRids(prev => new Map(prev).set(rec.rid, pct));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(xhr.response as Blob);
          } else {
            reject(new Error(`HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recording-${rec.rid}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloadingRids(prev => {
        const next = new Map(prev);
        next.delete(rec.rid);
        return next;
      });
    }
  }, []);

  return (
    <div className="history-detail-panel">
      <div className="detail-header">
        <h2 className="detail-header__title">Call Details</h2>
        <button
          className="detail-close-btn"
          onClick={onClose}
          aria-label="Close detail panel">
          <Icon name="close" size={18} fill="currentColor" />
        </button>
      </div>

      <div className="detail-body">
        {/* Overview */}
        <div className="detail-section">
          <h3 className="detail-section__title">Overview</h3>
          <div className="detail-info-grid">
            <div className="detail-info-card">
              <span className="detail-info-card__label">Status</span>
              <span
                className={`detail-status-badge ${getStatusBadgeClass(call.status || '')}`}>
                <span className="detail-status-dot" />
                {call.status || 'Unknown'}
              </span>
            </div>
            <div className="detail-info-card">
              <span className="detail-info-card__label">Duration</span>
              <span className="detail-info-card__value">
                {call.totalDuration}
              </span>
            </div>
            <div className="detail-info-card">
              <span className="detail-info-card__label">Started</span>
              <span className="detail-info-card__value">
                {formatDate(call.startedAt)} {formatTime(call.startedAt)}
              </span>
            </div>
            <div className="detail-info-card">
              <span className="detail-info-card__label">Ended</span>
              <span className="detail-info-card__value">
                {call.endedAt
                  ? `${formatDate(call.endedAt)} ${formatTime(call.endedAt)}`
                  : '—'}
              </span>
            </div>
            <div className="detail-info-card">
              <span className="detail-info-card__label">Mode</span>
              <span className="detail-info-card__value">
                {call.mode || '—'}
              </span>
            </div>
            <div className="detail-info-card">
              <span className="detail-info-card__label">Type</span>
              <span className="detail-info-card__value">
                {call.type || '—'}
              </span>
            </div>
          </div>
        </div>

        {/* Session ID */}
        <div className="detail-section">
          <h3 className="detail-section__title">Session ID</h3>
          <div className="detail-session-id">
            {call.sessionId.split('.').pop()}
          </div>
        </div>

        {/* Participants */}
        <div className="detail-section">
          <h3 className="detail-section__title">
            Participants ({call.totalParticipants})
          </h3>
          <div className="detail-participant-list">
            {call.participants.map((p, i) => (
              <div key={`${p.uid}-${i}`} className="detail-participant-row">
                <div className="detail-participant-avatar">
                  <Avatar name={p.name} imageUrl={p.avatar} size={36} />
                </div>
                <div className="detail-participant-info">
                  <div className="detail-participant-name">{p.name}</div>
                  <div className="detail-participant-meta">
                    {p.uid}
                    {p.state ? ` · ${p.state}` : ''}
                  </div>
                </div>
                <div className="detail-participant-duration">
                  {formatDurationMinutes(p.totalDurationInMinutes)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recordings */}
        <div className="detail-section">
          <h3 className="detail-section__title">Recordings</h3>
          {call.recordings && call.recordings.length > 0 ? (
            <div className="detail-recording-list">
              {call.recordings.map(rec => {
                const progress = downloadingRids.get(rec.rid);
                const isDownloading = progress !== undefined;
                return (
                  <div key={rec.rid} className="detail-recording-row">
                    <div className="detail-recording-icon">
                      <Icon
                        name="video-fill"
                        size={18}
                        fill="var(--cometchat-calls-primary-color)"
                      />
                    </div>
                    <div className="detail-recording-info">
                      <div className="detail-recording-name">Recording</div>
                      <div className="detail-recording-meta">
                        {rec.duration
                          ? formatDurationMinutes(rec.duration / 60)
                          : 'Unknown duration'}
                        {rec.startTime ? ` · ${formatTime(rec.startTime)}` : ''}
                      </div>
                    </div>
                    <button
                      className="detail-play-btn"
                      onClick={() =>
                        setPlayingUrl(
                          rec.recording_url.replace(
                            'recordings-us.cometchat-staging.com',
                            'recordings.rtcv5-us.cometchat-staging.com',
                          ),
                        )
                      }
                      aria-label="Play recording">
                      <Icon
                        name="play-fill"
                        size={18}
                        fill="var(--cometchat-calls-primary-color)"
                      />
                    </button>
                    <button
                      className="detail-download-btn"
                      onClick={() => handleDownload(rec)}
                      disabled={isDownloading}
                      aria-label="Download recording">
                      {isDownloading ? (
                        <svg
                          className="detail-progress-ring"
                          width="22"
                          height="22"
                          viewBox="0 0 22 22">
                          <circle
                            className="detail-progress-ring__bg"
                            cx="11"
                            cy="11"
                            r="9"
                            fill="none"
                            strokeWidth="2"
                          />
                          <circle
                            className="detail-progress-ring__fg"
                            cx="11"
                            cy="11"
                            r="9"
                            fill="none"
                            strokeWidth="2"
                            strokeDasharray={`${2 * Math.PI * 9}`}
                            strokeDashoffset={`${2 * Math.PI * 9 * (1 - (progress ?? 0) / 100)}`}
                            strokeLinecap="round"
                          />
                        </svg>
                      ) : (
                        <Icon
                          name="download"
                          size={18}
                          fill="var(--cometchat-calls-primary-color)"
                        />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="detail-no-recordings">
              No recordings for this session
            </span>
          )}
        </div>
      </div>
      {playingUrl && (
        <VideoPlayerDialog
          url={playingUrl}
          onClose={() => setPlayingUrl(null)}
        />
      )}
    </div>
  );
}

export default CallDetails;
